import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Tooltip } from '~/components/Tooltip';
import { StripedContainer } from '~/components/striped-container';
import { Content, Label } from './styles';

export interface StripedCardProps {
  content: ReactNode;
  label?: string;
}

export const StripedCard = ({ content, label }: StripedCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [content]);

  const tooltipText =
    typeof content === 'string' || typeof content === 'number' ? String(content) : null;
  const showsTooltip = tooltipText !== null && overflowing;

  // A clipped value is only readable through the tooltip, so the card joins the tab order while it
  // overflows and the tooltip opens on focus as well as hover.
  const card = (
    <StripedContainer
      tabIndex={showsTooltip ? 0 : undefined}
      sx={{ '&:focus-visible': (theme) => theme.mixins.focusRing }}
    >
      {label && <Label>{label}</Label>}
      <Content ref={ref}>{content}</Content>
    </StripedContainer>
  );

  if (tooltipText === null) return card;

  return (
    <Tooltip
      title={tooltipText}
      placement="top-start"
      disableFocusListener={!showsTooltip}
      disableHoverListener={!showsTooltip}
    >
      {card}
    </Tooltip>
  );
};
